import React from 'react';
import { injectIntl } from 'react-intl';
import AdminShell, { Icon } from './AdminShell';
import DataTable from './DataTable';
import { Modal, Summary } from './Overlay';
import { Fieldset, SettingRow, Switch, Field } from './Form';
import { text } from './peopleCopy';

/*
 * Promo codes — layar `promos` paket client.
 *
 * Dua hal berbeda dari prototipe, keduanya karena datanya:
 *
 *   - TIDAK ADA BATAS PENEBUSAN. Prototipe menampilkan "412 / 1000" dan status
 *     'exhausted'. PromoCode tidak punya kolom batas, jadi sebuah kode berlaku
 *     sampai kedaluwarsa atau dimatikan — tidak pernah habis sendiri. Kolomnya
 *     menampilkan jumlah penebusan saja, dan itu disebut apa adanya.
 *
 *   - MENYIMPAN MENGIRIM PUSH KE SELURUH PELANGGAN. addPromoCode memanggil
 *     sendNotifications setiap kali kode dibuat, dan setiap kali kode yang
 *     aktif diubah. Itu tindakan yang menjangkau keluar dan tidak bisa
 *     dibatalkan, jadi tombolnya menyebutkannya sebelum ditekan — bukan setelah
 *     seseorang membetulkan salah ketik dan mengirim notifikasi ke semua orang.
 */

const gql = (query, variables) => fetch('/graphql', {
  method: 'post',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }),
  credentials: 'include'
}).then(r => r.json());

const Q = `query ($currentPage: Int, $limit: Int, $searchList: String, $state: String) {
  getAdminPromoCodes(currentPage: $currentPage, limit: $limit, searchList: $searchList, state: $state) {
    count total liveCount status errorMessage
    results {
      id title description code type promoValue currency expiryDate isEnable
      state redemptions lastRedeemedAt discountGiven
    }
  }
}`;

// addPromoCode menangani buat DAN sunting: id kosong berarti membuat.
const M_SAVE = `mutation ($id: Int, $title: String!, $description: String!, $code: String!,
                 $type: Int, $promoValue: Float!, $currency: String,
                 $expiryDate: String, $isEnable: Boolean) {
  addPromoCode(id: $id, title: $title, description: $description, code: $code,
               type: $type, promoValue: $promoValue, currency: $currency,
               expiryDate: $expiryDate, isEnable: $isEnable) { status errorMessage }
}`;

const M_DELETE = `mutation ($id: Int!) {
  deletePromoCode(id: $id) { status errorMessage }
}`;

const ok = res => res && Number(res.status) === 200;

const STATE_META = {
  live: ['Live', 'ok'],
  disabled: ['Disabled', ''],
  expired: ['Expired', 'danger']
};

const day = v => {
  const d = new Date(Number(v) || v);
  return isNaN(d) ? null : d.toISOString().slice(0, 10);
};

const blank = () => ({
  id: null, title: '', description: '', code: '', type: 1, promoValue: 10,
  currency: 'AED', expiryDate: '', isEnable: true
});

class PromoCodes extends React.Component {
  state = {
    rows: [], count: 0, total: 0, liveCount: 0,
    page: 1, perPage: 25, search: '', filterState: '',
    loading: true, error: null,
    form: null, formError: null, deleting: null, saving: false, notice: null
  };

  componentDidMount() { this.load(); }

  locale = () => this.props.intl && this.props.intl.locale;
  tx = (en, ar) => text(this.locale(), en, ar);

  vars = extra => ({
    currentPage: this.state.page,
    limit: this.state.perPage,
    searchList: this.state.search || undefined,
    state: this.state.filterState || undefined,
    ...extra
  });

  load = async quiet => {
    if (!quiet) this.setState({ loading: true, error: null });
    const json = await gql(Q, this.vars()).catch(() => null);
    const res = json && json.data && json.data.getAdminPromoCodes;
    if (!ok(res)) {
      this.setState({ loading: false, error: (res && res.errorMessage) || this.tx('Could not load promo codes.', 'تعذر تحميل الرموز الترويجية.') });
      return;
    }
    this.setState({
      rows: res.results || [], count: res.count || 0,
      total: res.total || 0, liveCount: res.liveCount || 0, loading: false
    });
  };

  setPage = page => this.setState({ page }, () => this.load());
  setPerPage = perPage => this.setState({ perPage, page: 1 }, () => this.load());
  setSearch = search => this.setState({ search, page: 1 }, () => this.load());
  pick = key => e => this.setState({ [key]: e.target.value, page: 1 }, () => this.load());

  openAdd = () => this.setState({ form: blank(), formError: null, notice: null });

  openEdit = r => this.setState({
    form: {
      id: r.id, title: r.title || '', description: r.description || '', code: r.code,
      type: r.type, promoValue: r.promoValue, currency: r.currency || 'AED',
      expiryDate: day(r.expiryDate) || '', isEnable: r.isEnable
    },
    formError: null, notice: null
  });

  setForm = (key, value) => this.setState(st => ({ form: { ...st.form, [key]: value } }));

  submit = async () => {
    const f = this.state.form;
    if (!f.code.trim() || !f.title.trim() || !f.description.trim()) {
      this.setState({ formError: this.tx('Code, title and description are all required.', 'الرمز والعنوان والوصف مطلوبة.') });
      return;
    }
    if (!(Number(f.promoValue) > 0)) {
      this.setState({ formError: this.tx('The discount must be more than zero.', 'يجب أن يكون الخصم أكبر من صفر.') });
      return;
    }
    if (Number(f.type) === 1 && Number(f.promoValue) > 100) {
      this.setState({ formError: this.tx('A percentage discount cannot be more than 100.', 'لا يمكن أن يتجاوز الخصم النسبي 100.') });
      return;
    }
    this.setState({ saving: true, formError: null });

    const json = await gql(M_SAVE, {
      id: f.id || undefined,
      title: f.title.trim(),
      description: f.description.trim(),
      // Kode ditulis huruf besar tanpa spasi, seperti prototipe: pelanggan
      // mengetiknya, dan "bas 20" tidak akan pernah cocok dengan "BAS20".
      code: f.code.trim().toUpperCase().replace(/\s+/g, ''),
      type: Number(f.type),
      promoValue: Number(f.promoValue),
      currency: f.currency || 'AED',
      expiryDate: f.expiryDate || undefined,
      isEnable: !!f.isEnable
    }).catch(() => null);
    const res = json && json.data && json.data.addPromoCode;
    this.setState({ saving: false });
    if (!ok(res)) {
      this.setState({ formError: (res && res.errorMessage) || this.tx('Could not save the code.', 'تعذر حفظ الرمز.') });
      return;
    }
    this.setState({
      form: null,
      notice: {
        tone: 'info',
        text: (f.id ? this.tx('Code updated. ', 'تم تحديث الرمز. ') : this.tx('Code created. ', 'تم إنشاء الرمز. '))
          + (f.isEnable
            ? this.tx('A push notification was sent to every customer.', 'تم إرسال إشعار فوري إلى كل عميل.')
            : this.tx('It is disabled, so no notification was sent.', 'الرمز متوقف، لذلك لم يُرسل أي إشعار.'))
      }
    });
    this.load(true);
  };

  remove = async () => {
    const r = this.state.deleting;
    this.setState({ saving: true });
    const json = await gql(M_DELETE, { id: r.id }).catch(() => null);
    const res = json && json.data && json.data.deletePromoCode;
    this.setState({ saving: false, deleting: null });
    this.setState({
      notice: !ok(res)
        ? { tone: 'danger', text: (res && res.errorMessage) || this.tx('Could not delete the code.', 'تعذر حذف الرمز.') }
        : { tone: 'danger', text: `${r.code} ${this.tx('was deleted. Past redemptions keep their record.', 'تم حذفه. تبقى عمليات الاسترداد السابقة مسجلة.')}` }
    });
    this.load(true);
  };

  discountOf = r => (r.type === 1
    ? `${r.promoValue}%`
    : `${r.currency || 'AED'} ${r.promoValue}`);

  columns() {
    const t = this.tx;
    return [
      {
        key: 'code', label: t('Code', 'الرمز'), sortable: true,
        render: r => (
          <span>
            <span className="mono" style={{ fontWeight: 700 }}>{r.code}</span>
            {r.title && (
              <span className="muted" style={{ display: 'block', fontSize: 'var(--fs-xs)' }}>
                {r.title}
              </span>
            )}
          </span>
        )
      },
      {
        key: 'type', label: t('Type', 'النوع'),
        render: r => (r.type === 1 ? t('Percentage', 'نسبي') : t('Fixed', 'ثابت'))
      },
      {
        key: 'promoValue', label: t('Discount', 'الخصم'), align: 'end', sortable: true,
        render: r => <span className="num">{this.discountOf(r)}</span>
      },
      {
        // Prototipe menulis "412 / 1000". Tidak ada batas yang tersimpan, jadi
        // menampilkan penyebut apa pun akan mengarang aturan yang tidak ada.
        key: 'redemptions', label: t('Redeemed', 'مسترد'), align: 'end', sortable: true,
        render: r => r.redemptions
          ? (
            <span className="num" title={r.lastRedeemedAt ? `${t('Last used', 'آخر استخدام')} ${day(r.lastRedeemedAt)}` : ''}>
              {r.redemptions}
            </span>
          )
          : <span className="muted">{t('Never', 'أبداً')}</span>
      },
      {
        key: 'state', label: t('Status', 'الحالة'),
        render: r => {
          const [label, kind] = STATE_META[r.state] || [r.state, ''];
          const ar = { Live: 'نشط', Disabled: 'متوقف', Expired: 'منتهي' }[label] || label;
          return <span className={`tag ${kind}`}><i />{t(label, ar)}</span>;
        }
      },
      {
        key: 'expiryDate', label: t('Expires', 'ينتهي'), align: 'end', sortable: true,
        render: r => r.expiryDate
          ? <span className="mono">{day(r.expiryDate)}</span>
          : <span className="muted">{t('Never', 'أبداً')}</span>
      },
      {
        key: 'act', label: '', align: 'end',
        render: r => (
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <button className="btn icon" type="button" title="Edit code"
              aria-label={`Edit ${r.code}`} onClick={() => this.openEdit(r)}>
              <Icon name="edit" />
            </button>
            <button className="btn icon" type="button" title="Delete code"
              aria-label={`Delete ${r.code}`}
              onClick={() => this.setState({ deleting: r, notice: null })}>
              <Icon name="trash" />
            </button>
          </span>
        )
      }
    ];
  }

  renderForm() {
    const f = this.state.form;
    const { saving, formError } = this.state;
    const pct = Number(f.type) === 1;
    const t = this.tx;

    return (
      <Modal
        icon="tag" wide
        title={f.id ? `${t('Edit', 'تعديل')} ${f.code}` : t('Create promo code', 'إنشاء رمز ترويجي')}
        sub={t('Customers type the code at checkout.', 'يدخل العملاء الرمز عند إتمام الدفع.')}
        confirmLabel={f.id ? t('Save code', 'حفظ الرمز') : t('Create code', 'إنشاء الرمز')}
        busy={saving}
        disabled={!f.code.trim() || !f.title.trim() || !f.description.trim()}
        onConfirm={this.submit}
        onClose={() => this.setState({ form: null, formError: null })}
      >
        {formError && (
          <div className="notice danger">
            <span className="ni">!</span><span className="nt">{formError}</span>
          </div>
        )}

        {/* Efek samping yang menjangkau keluar. Disebut sebelum tombolnya
            ditekan, bukan sesudah. */}
        {f.isEnable && (
          <div className="notice danger">
            <span className="ni">!</span>
            <span className="nt">
              {t('Saving an enabled code sends a push notification to every customer, with the title and description below as its text. That cannot be undone. Switch the code off below to save without notifying anyone.', 'حفظ رمز مفعّل يرسل إشعاراً فورياً إلى كل عميل باستخدام العنوان والوصف أدناه. لا يمكن التراجع عن ذلك. أوقف الرمز أدناه للحفظ دون إرسال إشعار.')}
            </span>
          </div>
        )}

        <Fieldset title={t('Code', 'الرمز')}>
          <Field label={t('Code', 'الرمز')} required hint={t('Upper-cased and stripped of spaces on save.', 'يُحفظ بأحرف كبيرة مع إزالة المسافات.') }>
            <input className="inp mono" value={f.code} placeholder="SUMMER26"
              onChange={e => this.setForm('code', e.target.value)} />
          </Field>
          <Field label={t('Discount type', 'نوع الخصم')}>
            <select className="inp" value={f.type}
              onChange={e => this.setForm('type', Number(e.target.value))}>
              <option value={1}>{t('Percentage off the fare', 'خصم نسبي من الأجرة')}</option>
              <option value={2}>{t('Fixed amount off', 'خصم مبلغ ثابت')}</option>
            </select>
          </Field>
          <Field label={pct ? t('Percentage off', 'الخصم النسبي') : `${t('Amount off', 'مبلغ الخصم')} (${f.currency})`} required>
            <input className="inp" type="number" min="0" max={pct ? 100 : undefined}
              step={pct ? 1 : 0.5} value={f.promoValue}
              onChange={e => this.setForm('promoValue', e.target.value)} />
          </Field>
          <Field label={t('Currency', 'العملة')}
            hint={pct
              ? t('Not used by a percentage discount, but stored with the code.', 'لا تُستخدم مع الخصم النسبي، لكنها تُحفظ مع الرمز.')
              : t('The fixed amount is in this currency.', 'المبلغ الثابت بهذه العملة.') }>
            <input className="inp" value={f.currency} placeholder="AED"
              onChange={e => this.setForm('currency', e.target.value.toUpperCase())} />
          </Field>
          <Field label={t('Expires on', 'تاريخ الانتهاء')}
            hint={t('Leave blank for a code that never expires.', 'اتركه فارغاً لرمز لا ينتهي.') }>
            <input className="inp" type="date" value={f.expiryDate}
              onChange={e => this.setForm('expiryDate', e.target.value)} />
          </Field>
        </Fieldset>

        <Fieldset title={t('Notification text', 'نص الإشعار')}
          desc={t('These two lines are what customers see in the push notification.', 'يرى العملاء هذين السطرين في الإشعار الفوري.') }>
          <Field label={t('Title', 'العنوان')} required span>
            <input className="inp" value={f.title} placeholder="20% off your next tow"
              onChange={e => this.setForm('title', e.target.value)} />
          </Field>
          <Field label={t('Description', 'الوصف')} required span>
            <textarea className="inp" rows={2} value={f.description}
              placeholder="Use code SUMMER26 at checkout before 31 August."
              onChange={e => this.setForm('description', e.target.value)} />
          </Field>
        </Fieldset>

        <Fieldset title={t('Availability', 'التوفر')} rows>
          <SettingRow label={t('Enabled', 'مفعّل')}
            hint={t('A disabled code is rejected at checkout. There is no redemption limit — a code runs until it expires or is switched off.', 'يُرفض الرمز المتوقف عند الدفع. لا يوجد حد للاسترداد؛ يستمر الرمز حتى ينتهي أو يتم إيقافه.') }>
            <Switch checked={f.isEnable} label={t('Enabled', 'مفعّل')}
              onChange={v => this.setForm('isEnable', v)} />
          </SettingRow>
        </Fieldset>
      </Modal>
    );
  }

  render() {
    const {
      rows, count, total, liveCount, page, perPage, loading, error,
      filterState, form, deleting, saving, notice
    } = this.state;
    const t = this.tx;

    return (
      <AdminShell
        current="promos"
        breadcrumb={[t('Catalogue', 'الكتالوج'), t('Promo codes', 'الرموز الترويجية')]}
        title={t('Promo codes', 'الرموز الترويجية')}
        lead={this.locale() === 'ar' ? `${total} رمز، ${liveCount} نشط حالياً.` : `${total} code${total === 1 ? '' : 's'}, ${liveCount} live right now.`}
        actions={[
          <a className="btn ghost" href="/siteadmin/promo-code/list" key="c">{t('Open in classic', 'فتح النسخة الكلاسيكية')}</a>,
          <button className="btn" type="button" key="a" onClick={this.openAdd}>
            <Icon name="plus" />{t('Create code', 'إنشاء رمز')}
          </button>
        ]}
      >
        {notice && (
          <div className={`notice ${notice.tone}`}>
            <span className="ni">{notice.tone === 'danger' ? '!' : '✓'}</span>
            <span className="nt">{notice.text}</span>
            <button className="btn ghost sm" type="button"
              onClick={() => this.setState({ notice: null })}>{t('Dismiss', 'إخفاء')}</button>
          </div>
        )}

        <div className="notice">
          <span className="ni">i</span>
          <span className="nt">
            {t('There is no redemption cap in this system: a code runs until its expiry date or until it is switched off. Saving an enabled code push-notifies every customer.', 'لا يوجد حد للاسترداد في هذا النظام؛ يستمر الرمز حتى تاريخ الانتهاء أو إيقافه. حفظ رمز مفعّل يرسل إشعاراً فورياً إلى كل عميل.')}
          </span>
        </div>

        {form && this.renderForm()}

        {deleting && (
          <Modal
            icon="trash" tone="danger"
            title={`${t('Delete', 'حذف')} ${deleting.code}؟`}
            sub={deleting.title || t('Promo code', 'رمز ترويجي')}
            confirmLabel={t('Delete code', 'حذف الرمز')}
            busy={saving}
            onConfirm={this.remove}
            onClose={() => this.setState({ deleting: null })}
          >
            <Summary pairs={[
              [t('Discount', 'الخصم'), this.discountOf(deleting)],
              [t('Redeemed', 'مسترد'), deleting.redemptions ? `${deleting.redemptions} ${t('times', 'مرات')}` : t('Never', 'أبداً')],
              [t('Expires', 'ينتهي'), day(deleting.expiryDate) || t('Never', 'أبداً')]
            ]} />
            <span className="hint">
              {/* BookingPromoCode menyimpan salinan kode, nilai dan jenisnya saat
                  dipakai, jadi riwayat diskon tetap utuh setelah barisnya hilang. */}
              {t('Past redemptions keep their own copy of the code and its value, so the history stays intact. Customers who try the code after this will be rejected at checkout.', 'تحتفظ عمليات الاسترداد السابقة بنسخة من الرمز وقيمته، لذلك يبقى السجل محفوظاً. سيُرفض الرمز عند الدفع بعد حذفه.')}
            </span>
          </Modal>
        )}

        {error
          ? <div className="notice danger"><span className="ni">!</span><span className="nt">{error}</span></div>
          : (
            <DataTable
              columns={this.columns()}
              rows={rows}
              count={count}
              page={page}
              perPage={perPage}
              loading={loading}
              onPage={this.setPage}
              onPerPage={this.setPerPage}
              onSearch={this.setSearch}
              searchPlaceholder={t('Search code, title or description', 'ابحث عن الرمز أو العنوان أو الوصف')}
              filters={
                <select className="inp" value={filterState} onChange={this.pick('filterState')}
                  aria-label={t('Filter by status', 'تصفية حسب الحالة')}>
                  <option value="">{t('Status: all', 'الحالة: الكل')}</option>
                  <option value="live">{t('Live', 'نشط')}</option>
                  <option value="disabled">{t('Disabled', 'متوقف')}</option>
                  <option value="expired">{t('Expired', 'منتهي')}</option>
                </select>
              }
              emptyText={t('No promo codes', 'لا توجد رموز ترويجية')}
              emptyHint={t('Create one to run a discount campaign.', 'أنشئ رمزاً لبدء حملة خصم.')}
            />
          )}
      </AdminShell>
    );
  }
}

export default injectIntl(PromoCodes);
